import { Col, Modal, Row, Typography, Table, Button } from 'antd';

import { useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { applyMask } from '@/utils/mask';

import { cpfMask } from '@/utils/mask/commonMasks';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { ModalProps } from '@/types/ModalProps';
import {
  Employee,
  ListEmployeesQuery,
} from '@/core/domain/enterprise/Employees/Employees.types';
import { useEmployees } from '@/core/domain/enterprise/Employees/Employees.hooks';

import LoadMoreSummary from '../LoadMoreSummary';
import EmployeesSelectedFilters from './EmployeesSelectedFilters';
import { CheckOutlined } from '@ant-design/icons';

interface SelectedEmployeesProps extends ModalProps {
  finishCallback?: (employeeIds?: number[]) => void;
  selectedEmployeesIds?: number[];
  noSelection?: boolean;
  onEmployeeSelect?: (employee: Employee) => void;
  queryFilter?: ListEmployeesQuery;
}

const EmployeesSelectedModal: React.FC<SelectedEmployeesProps> = ({
  visible,
  finishCallback,
  handleVisible,
  selectedEmployeesIds = [],
  noSelection,
  onEmployeeSelect,
  queryFilter,
}) => {
  const intl = useIntl();

  const { Title } = Typography;

  const [queryParams, setQueryParams] = useState<ListEmployeesQuery>({
    ...queryFilter,
    order: 'ASC',
    isActive: true,
  });

  const [selectedEmployeesKeys, setSelectedEmployeesKeys] = useState<Key[]>([]);
  const { data, isFetching, hasNextPage, fetchNextPage } = useEmployees(
    queryParams,
    visible,
  );

  const employees = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      setSelectedEmployeesKeys(selectedEmployeesIds);
    }
  }, [visible]);

  const handleOk = (): void => {
    if (finishCallback) {
      finishCallback(selectedEmployeesKeys as number[]);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams({ ...queryFilter, order: 'ASC', isActive: true });

    setSelectedEmployeesKeys([]);

    handleVisible();
  };

  const columns: ColumnsType<Employee> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'employee.list.header.name' }),
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'cpf',
      title: intl.formatMessage({ id: 'employee.personal.form.cpf' }),
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        if (value?.length === 11) {
          return applyMask(value, cpfMask);
        }
        return value;
      },
      ellipsis: true,
      width: 220,
    },
  ];

  const columnsSelect: ColumnsType<Employee> = [
    ...columns,
    {
      dataIndex: 'actions',
      align: 'right',
      render: (_, row) => (
        <Button
          type="primary"
          onClick={onEmployeeSelect ? () => onEmployeeSelect(row) : () => null}
          icon={<CheckOutlined />}
        ></Button>
      ),
      width: 140,
    },
  ];

  const handleEmployeesSelect = (selectedRowKeys: Key[]) => {
    setSelectedEmployeesKeys(selectedRowKeys);
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'employee.modal.selectedEmployees.title.add',
      })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={700}
      destroyOnClose
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                {intl.formatMessage({
                  id: 'employee.modal.selectedEmployees.search',
                })}
              </Title>
            </div>

            <EmployeesSelectedFilters setQueryParams={setQueryParams} />

            <Table<Employee>
              columns={onEmployeeSelect ? columnsSelect : columns}
              dataSource={employees}
              loading={isFetching}
              rowKey={(row) => row.id}
              pagination={false}
              summary={() =>
                hasNextPage ? (
                  <LoadMoreSummary
                    onClick={fetchNextPage}
                    columnsCount={columns.length + 1}
                  />
                ) : null
              }
              rowSelection={
                !noSelection
                  ? {
                      selectedRowKeys: selectedEmployeesKeys,
                      onChange: handleEmployeesSelect,
                      preserveSelectedRowKeys: true,
                    }
                  : undefined
              }
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default EmployeesSelectedModal;
